import React, { useState, useEffect } from "react";

import { Outlet, Navigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";


import Header from "portal/components/Header/Header";
import Sidebar from "portal/components/SideBar/SideBar";
import InsightDrawer from "portal/components/InsightDrawer";
import AIAssistantDrawer from "portal/components/Assistant/AIAssistantDrawer";
import PatientDetailSlidePanel from "portal/components/PatientDetailSlidePanel";
import SubscriptionProvider from "context/SubscriptionProvider";
import ImpersonationBanner from "portal/components/ImpersonationBanner";
import DemoBanner from "portal/components/DemoBanner";
import MobileBottomNav from "portal/components/MobileBottomNav";
import SubscriptionBanner from "portal/components/SubscriptionBanner";
import GlobalSessionAnnouncer from "portal/components/GlobalSessionAnnouncer";
import UnmarkedAttendancePopup from "portal/components/UnmarkedAttendancePopup";
import { NotificationsProvider } from "portal/hooks/useNotifications";
import allRoutes from "client/constants/allRoutes";

const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const user = useSelector((state) => state.auth.user);
  const isImpersonating = useSelector((state) => state.auth.isImpersonating);
  // The Dashboard's Booked Sessions opens in its own tab as /portal?view=sessions
  // — a board to keep on screen, so the navigation is left out there.
  const location = useLocation();
  const boardOnly = new URLSearchParams(location.search).get('view') === 'sessions';

  // Screenshot mode — hides floating widgets / banners for clean marketing captures.
  // Toggle in browser console: localStorage.setItem('ss_mode','1') / removeItem('ss_mode')
  const ssMode = typeof window !== 'undefined' && localStorage.getItem('ss_mode') === '1';

  // Add portal-active class to block page-level scroll (portal has its own scroll)
  // and to scope the portal's 90% density (html.portal-active, index.css) away from marketing pages.
  useEffect(() => {
    document.documentElement.classList.add('portal-active');
    document.body.classList.add('portal-active');
    return () => {
      document.documentElement.classList.remove('portal-active');
      document.body.classList.remove('portal-active');
    };
  }, []);

  // Force password change before accessing portal
  if (user?.must_change_password === 1) {
    return <Navigate to={allRoutes.forceChangePassword} replace />;
  }

  // Super Admin should only use the Super Admin Panel (unless impersonating a clinic)
  if (user?.RoleId === 1 && !isImpersonating) {
    return <Navigate to={allRoutes.superadmin} replace />;
  }

  return (
    <SubscriptionProvider>
      {/* One badge poll and one smart-alerts fetch for the whole portal. Header,
          Dashboard and the Notifications page each used to run their own. */}
      <NotificationsProvider enabled={!boardOnly}>
      <div className="flex flex-col h-screen overflow-hidden">
        {!ssMode && <ImpersonationBanner />}
        {!ssMode && <DemoBanner />}
        <div className="flex flex-1 bg-surface-bg overflow-hidden min-h-0">
          {!boardOnly && <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />}

          <div className="flex flex-col flex-1 overflow-hidden bg-surface-card">
            {!boardOnly && <Header setSidebarOpen={setSidebarOpen} />}
            {!ssMode && <SubscriptionBanner />}

          <main className="flex-1 overflow-y-auto overflow-x-hidden scroll-gutter-stable bg-surface-bg px-page-x py-page-y min-h-0 pb-[calc(var(--page-padding-y,1.25rem)+60px)] lg:pb-[calc(var(--page-padding-y,1.25rem)+16px)]">
            <Outlet />
          </main>
        </div>
        </div>
      </div>
      {!boardOnly && <MobileBottomNav />}
      <InsightDrawer />
      {!ssMode && <AIAssistantDrawer />}
      <PatientDetailSlidePanel />
      {/* Its own /admin/appointment fetch (≈118 queries) has no place in the board tab. */}
      {!boardOnly && <GlobalSessionAnnouncer />}
      {!ssMode && !boardOnly && <UnmarkedAttendancePopup />}
      </NotificationsProvider>
    </SubscriptionProvider>
  );
};

export default Layout;
